import axios from 'axios';

import { downloadReportRunCsv, pollReportRun, submitReportRun } from '@/utils/reportRunsV3';

/**
 * Custom reports on an inventory view's Reports tab: the org's Actions query
 * templates (built in web-app-v3 → Chain Analytics → Templates) that were
 * added to inventory view reports. Listed from analytics-query-svc through the
 * API gateway; run through api-svc's `custom-actions-query` report run.
 */

/** Org flag gating Actions templates and custom inventory view reports. */
export const ANALYTICS_ACTIONS_FLAG = 'analytics-actions-reports';

/** The api-svc report run type that runs an Actions template for a view. */
export const CUSTOM_ACTIONS_REPORT_TYPE = 'custom-actions-query';

export interface CustomActionsReport {
  id: string;
  name: string;
  description: string;
  /** Set when the template reads one specific inventory view. */
  inventoryViewId?: string;
}

interface TemplateRecord {
  id: string;
  name: string;
  description?: string;
  dataSource?: string;
  inventoryReport?: boolean;
  inventoryViewId?: string;
}

/** The report-run calls a custom report makes, injectable so tests can use a fake api-svc. */
export interface ReportRunClient {
  submit: typeof submitReportRun;
  poll: typeof pollReportRun;
  download: typeof downloadReportRunCsv;
}

export const defaultReportRunClient: ReportRunClient = {
  submit: submitReportRun,
  poll: pollReportRun,
  download: downloadReportRunCsv,
};

/** What to tell the user about a run that ended `failed`: api-svc's recorded cause when it sent one. */
export function describeFailedRun(errors: string[] | undefined): string {
  return errors !== undefined && errors.length > 0
    ? `The report failed: ${errors.join('; ')}`
    : 'The report failed. Try again.';
}

/**
 * What to tell the user when submitting, polling or downloading threw.
 * api-svc's user-visible exceptions carry a `message` in the response body;
 * show it, so a refused submission reads as more than "try again later".
 */
export function describeRunError(err: unknown): string {
  const generic = "Couldn't run the report. Try again later.";
  const body = (err as { response?: { data?: unknown } })?.response?.data;
  const message = typeof body === 'object' && body !== null ? (body as { message?: unknown }).message : undefined;
  return typeof message === 'string' && message.trim().length > 0 ? `${generic} (${message.trim()})` : generic;
}

export interface CustomActionsReportSource {
  list(orgId: string, inventoryViewId: string): Promise<CustomActionsReport[]>;
}

/** The templates this view's Reports tab offers: added to reports, and for this view or any view. */
export function reportsForView(templates: TemplateRecord[], inventoryViewId: string): CustomActionsReport[] {
  return templates
    .filter(
      (t) =>
        t.dataSource === 'actions' &&
        t.inventoryReport === true &&
        (!t.inventoryViewId || t.inventoryViewId === inventoryViewId)
    )
    .map((t) => ({ id: t.id, name: t.name, description: t.description ?? '', inventoryViewId: t.inventoryViewId }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export class HttpCustomActionsReportSource implements CustomActionsReportSource {
  private readonly gatewayUrl: string | undefined;

  constructor(gatewayUrl: string | undefined = process.env.VUE_APP_GATEWAY_URL) {
    this.gatewayUrl = gatewayUrl;
  }

  public async list(orgId: string, inventoryViewId: string): Promise<CustomActionsReport[]> {
    // No gateway configured: offer no custom reports rather than guessing a host.
    if (!this.gatewayUrl) return [];
    const base = this.gatewayUrl.replace(/\/+$/, '');
    const resp = await axios.get(`${base}/v3/orgs/${encodeURIComponent(orgId)}/analytics/templates`, {
      withCredentials: true,
      // Resolve every status: a missing route or flag must not break the tab.
      validateStatus: () => true,
    });
    if (resp.status < 200 || resp.status >= 300 || !Array.isArray(resp.data)) return [];
    return reportsForView(resp.data as TemplateRecord[], inventoryViewId);
  }
}
